/**
 * Persistent left navigation rail — the studio's primary way to move
 * between tools (it replaces the old checklist home view).
 *
 * Layout role: the leftmost column of the workspace row
 * (sidebar → script view → feature panel → timeline dock at the bottom).
 *
 * Each creative tool is one editorial row. The active tool is bold; a
 * tool that already has content shows a trailing dot (•), derived from
 * the same `computeStepStatus` the rest of the studio uses so the dots
 * never disagree with reality.
 *
 * Two "separable" surfaces — the Script column and the Timeline dock —
 * are exposed as toggles in a footer group, since the user can hide
 * either when they're not needed.
 */

import {
  PANEL_REGISTRY,
  type PanelManager,
  type StudioPanelId,
} from "./PanelManager";
import type { ScriptDock } from "./ScriptDock";
import { computeStepStatus, STATUS_STORE_KEYS } from "./studioStatus";
import type { StudioStore } from "./StudioStore";
import type { Timeline } from "./Timeline";
import "./styles/sidebar.css";

interface SidebarItem {
  id: StudioPanelId;
  label: string;
}

/**
 * The creative tools, in working order. Script (its own column) and the
 * Timeline (the bottom dock) are intentionally absent — they live as
 * footer toggles below. Labels are short by design (matches the rail
 * mockup); the full titles still live in PANEL_REGISTRY for headers.
 */
const SIDEBAR_ITEMS: SidebarItem[] = [
  { id: "location", label: "Locations" },
  { id: "characters", label: "Characters" },
  { id: "costume", label: "Costume" },
  { id: "assets", label: "Assets" },
  { id: "storyboard", label: "Storyboard" },
  { id: "moodboard", label: "Moodboard" },
  { id: "final_video", label: "Final cut" },
];

interface SidebarOptions {
  store: StudioStore;
  panelManager: PanelManager;
  scriptDock: ScriptDock;
  timeline: Timeline;
}

export class Sidebar {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private readonly scriptDock: ScriptDock;
  private readonly timeline: Timeline;

  private readonly root: HTMLElement;
  private readonly itemButtons = new Map<StudioPanelId, HTMLButtonElement>();
  private scriptToggle!: HTMLButtonElement;
  private timelineToggle!: HTMLButtonElement;

  private unsubscribers: (() => void)[] = [];

  constructor(options: SidebarOptions) {
    this.store = options.store;
    this.panelManager = options.panelManager;
    this.scriptDock = options.scriptDock;
    this.timeline = options.timeline;

    this.root = document.createElement("nav");
    this.root.className = "studio-sidebar";
    this.root.setAttribute("aria-label", "Tools");

    const eyebrow = document.createElement("span");
    eyebrow.className = "studio-sidebar-eyebrow";
    eyebrow.textContent = "tools";
    this.root.appendChild(eyebrow);

    const list = document.createElement("div");
    list.className = "studio-sidebar-list";
    for (const item of SIDEBAR_ITEMS) {
      list.appendChild(this.buildItem(item));
    }
    this.root.appendChild(list);

    this.root.appendChild(this.buildFooter());
  }

  /** The element to mount into the workspace row. */
  getElement(): HTMLElement {
    return this.root;
  }

  /** Wire live updates. Call once after mounting. */
  start(): void {
    // Active-tool highlight follows the panel manager.
    this.unsubscribers.push(
      this.panelManager.subscribe((next) => this.syncActive(next)),
    );
    const current = this.panelManager.getCurrentPanelId();
    if (current) this.syncActive(current);

    // Progress dots follow the store.
    for (const key of STATUS_STORE_KEYS) {
      this.unsubscribers.push(this.store.subscribe(key, () => this.syncDots()));
    }
    this.syncDots();
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }

  // ------------------------------------------------------------------
  // Build
  // ------------------------------------------------------------------

  private buildItem(item: SidebarItem): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "studio-sidebar-item";
    button.dataset.tool = item.id;
    button.title = PANEL_REGISTRY[item.id].description;

    const label = document.createElement("span");
    label.className = "studio-sidebar-item-label";
    label.textContent = item.label;

    const dot = document.createElement("span");
    dot.className = "studio-sidebar-item-dot";
    dot.setAttribute("aria-hidden", "true");

    button.appendChild(label);
    button.appendChild(dot);
    button.addEventListener("click", () => this.panelManager.open(item.id));

    this.itemButtons.set(item.id, button);
    return button;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement("div");
    footer.className = "studio-sidebar-footer";

    const label = document.createElement("span");
    label.className = "studio-sidebar-footer-label";
    label.textContent = "views";
    footer.appendChild(label);

    this.scriptToggle = this.buildToggle("Script", "script", () => {
      this.scriptDock.toggle();
    });
    this.syncToggle(this.scriptToggle, this.scriptDock.isOpen());

    this.timelineToggle = this.buildToggle("Timeline", "timeline", () => {
      this.timeline.toggle();
    });
    this.syncToggle(this.timelineToggle, this.timeline.isExpanded());

    footer.appendChild(this.scriptToggle);
    footer.appendChild(this.timelineToggle);
    return footer;
  }

  private buildToggle(
    label: string,
    key: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "studio-sidebar-toggle";
    button.dataset.toggle = key;

    const text = document.createElement("span");
    text.textContent = label;

    const state = document.createElement("span");
    state.className = "studio-sidebar-toggle-state";
    state.setAttribute("aria-hidden", "true");

    button.appendChild(text);
    button.appendChild(state);
    button.addEventListener("click", onClick);
    return button;
  }

  // ------------------------------------------------------------------
  // Sync (called by subscriptions + the dock/timeline state callbacks)
  // ------------------------------------------------------------------

  private syncActive(activePanelId: StudioPanelId): void {
    for (const [id, button] of this.itemButtons) {
      button.classList.toggle("is-active", id === activePanelId);
    }
  }

  private syncDots(): void {
    for (const [id, button] of this.itemButtons) {
      const status = computeStepStatus(this.store, id);
      button.dataset.status = status;
      button.classList.toggle("has-content", status !== "empty");
    }
  }

  /** Reflect the Script column's open state in its sidebar toggle. */
  setScriptOpen(open: boolean): void {
    this.syncToggle(this.scriptToggle, open);
  }

  /** Reflect the Timeline dock's expanded state in its sidebar toggle. */
  setTimelineExpanded(expanded: boolean): void {
    this.syncToggle(this.timelineToggle, expanded);
  }

  private syncToggle(button: HTMLButtonElement, on: boolean): void {
    button.classList.toggle("is-on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
  }
}
